import {createStore, StoreApi, useStore} from 'zustand';
import {
  AiFieldDefinition,
  AiFieldDefinitionConfig,
  isAiFieldDefinition,
} from '../../../shared/fields/enrichment';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {
  useCreateFieldDefinition,
  useSaveFieldDefinition,
} from '../../../hooks/api/fieldDefinitions';
import {assertNever, entries} from '../../../shared/util';
import {useAgentBuilderServerState} from './AgentBuilder.state';
import _ from 'lodash';
import {MetadataQueryKey} from '../../../context/MetadataContext';
import {useAgentFieldType} from './AgentFieldTypeContext';
import {
  AgentType,
  DataTypeMap,
  getAiFieldDefinition,
} from '../../../shared/agent';
import {TeamSizeParamsQueryKeys} from '../../../hooks/api/agents';

const isConfigEmpty = (config?: AiFieldDefinitionConfig) => {
  if (!config) {
    return true;
  }

  const {fieldType} = config;

  switch (fieldType) {
    case 'boolean':
    case 'rating':
    case 'estimate':
      return !config.prompt.trim();
    case 'category':
      return config.categories.length < 2;
    case 'legacyTeamSize':
    case 'legacyResearch':
      return !config.prompt.input.trim();
    case 'research':
      return !config.question.trim();
    // teamsize agent is in valid state with no config changes
    case 'teamSize':
      return false;

    default:
      assertNever(fieldType);
  }
};

interface FieldDefinitionEditorState {
  serverState: AiFieldDefinition | undefined;
  updateServerState: ({
    serverState,
    updateMode,
  }: {
    serverState: AiFieldDefinition;
    updateMode: 'force' | 'ifNoChanges';
  }) => void;

  hasChanges: () => boolean;
  reset: () => void;

  isSaveable: () => boolean;

  config: AiFieldDefinitionConfig | undefined;
  setConfig: (config: AiFieldDefinitionConfig | undefined) => void;
}

const createFieldDefinitionStore = () =>
  createStore<FieldDefinitionEditorState>()((set, get) => ({
    serverState: undefined,
    updateServerState: ({serverState, updateMode}) => {
      if (updateMode === 'ifNoChanges' && get().hasChanges()) {
        return;
      }

      set({
        serverState,
        config: serverState.config,
      });
    },

    hasChanges: () => {
      const {serverState, config} = get();
      if (!serverState) {
        return !isConfigEmpty(config);
      }

      return !_.isEqual(serverState.config, config);
    },
    reset: () =>
      set({
        serverState: undefined,
        config: undefined,
      }),

    isSaveable: () => !isConfigEmpty(get().config),
    config: undefined,
    setConfig: (config) => set({config}),
  }));

const fieldDefinitionEditorStores: Partial<
  Record<AgentType, StoreApi<FieldDefinitionEditorState>>
> = {};

/**
 * Get the analyst field type from the editor store (draft/unsaved state).
 * Returns undefined if the agent type has no analyst field.
 */
export const getAnalystFieldTypeFromStore = (agentType: AgentType) => {
  if (
    agentType !== 'analyst' &&
    agentType !== 'researchAndAnalyst' &&
    agentType !== 'legacyResearchAndAnalyst'
  ) {
    return;
  }

  return fieldDefinitionEditorStores['analyst']?.getState().config?.fieldType;
};

export const useFieldDefinitionEditorStore = () => {
  // Lookup or create a store for the current field type
  const fieldType = useAgentFieldType();
  fieldDefinitionEditorStores[fieldType] ??= createFieldDefinitionStore();
  const store = useStore(fieldDefinitionEditorStores[fieldType]!);

  const queryClient = useQueryClient();
  const serverState = useAgentBuilderServerState();
  const createFieldDefinition = useCreateFieldDefinition();
  const saveFieldDefinition = useSaveFieldDefinition();

  const publishChanges = useMutation({
    mutationFn: async () => {
      const {id: agentId, label: agentLabel} = serverState;
      if (!store.hasChanges()) {
        return store.serverState;
      }

      if (!store.isSaveable()) {
        throw new Error('Invalid field definition state');
      }

      const config = store.config!;
      const label = agentLabel;

      if (store.serverState) {
        if (!store.hasChanges()) {
          return store.serverState;
        }

        const fieldDefinition = {
          ...store.serverState,
          dataType: DataTypeMap[config.fieldType],
          config,
          label,
        };

        if (!isAiFieldDefinition(fieldDefinition)) {
          throw new Error('Invalid AI field definition');
        }

        return saveFieldDefinition.mutateAsync({
          fieldDefinition,
        });
      }

      return createFieldDefinition.mutateAsync({
        agentId,
        label,
        ...getAiFieldDefinition(config),
      });
    },
    onSuccess: (updatedFieldDefinition) => {
      if (!isAiFieldDefinition(updatedFieldDefinition)) {
        return;
      }

      if (
        updatedFieldDefinition.config.fieldType === 'legacyResearch' ||
        updatedFieldDefinition.config.fieldType === 'research'
      ) {
        // Re-fetch metadata since dependent field definition configs have changed
        queryClient.invalidateQueries({
          queryKey: [MetadataQueryKey],
        });
      }

      if (updatedFieldDefinition.config.fieldType === 'legacyTeamSize') {
        // invalidate team size params because they a new prompt might cause them to change
        queryClient.invalidateQueries({
          queryKey: TeamSizeParamsQueryKeys.field(updatedFieldDefinition.id),
        });
      }

      store.updateServerState({
        serverState: updatedFieldDefinition,
        updateMode: 'force',
      });
    },
  });

  return {
    ...store,
    publishChanges,
  };
};

export function resetAllFieldDefinitionEditorStores() {
  entries(fieldDefinitionEditorStores).forEach(([, store]) => {
    store?.getState().reset();
  });
}
